import * as H from 'history';
import isUndefined from 'lodash/isUndefined';
import isEmpty from 'lodash/isEmpty';
import { SESSION_KEY_LABEL } from "@constants";
import { IAccount, IClientCash, IPublicPrice, ISessionKey, IUserWallet } from '@share/common-types';
import { IFullPublicPrice, IHotelInfo } from '@common-types';
import { countriesCode } from '@share/constants';
import { getHotelRoutePath, UrlUtils } from '@share/utils';
import { ROOMS_SEARCH_LABEL, SESSION_KEY_LABEL_ROOMS } from '@share/constants';

import { decodeHtmlEntities } from './vacation-rental-description';

export const getTopSuppliers = (
  prices: IPublicPrice[] = []
): IFullPublicPrice[] => {
  const res: IFullPublicPrice[] = [];
  const currentFullPrices: IFullPublicPrice[] = prices
    .map(({ price, supplierId }) => {
      return {
        price,
        supplierId,
      };
    })
    .filter((item) => item !== undefined);

  currentFullPrices.forEach((fullPrice: IFullPublicPrice) => {
    if (res.every((price: IFullPublicPrice) => price?.supplierId !== fullPrice?.supplierId)) {
      res.push(fullPrice);
    }
  });

  return res;
};

/**
 * Property names arrive HTML-escaped ("Mickey&#39;s Fantasia") and are rendered as text
 * rather than markup, so the escaping shows up verbatim wherever a name appears. Applied
 * where a name enters the store, since search, static content and availability all carry
 * the same one and any of them can be the last to write it.
 */
export const decodeHotelName = <T extends string | null | undefined>(name: T): T =>
  (typeof name === 'string' ? decodeHtmlEntities(name) : name) as T;

/** The same, for a list of search results, where the name is `displayName`. */
export const decodeHotelNames = <T extends { displayName?: string }>(hotels: T[]): T[] =>
  (hotels || []).map((hotel) => ({ ...hotel, displayName: decodeHotelName(hotel?.displayName) }));

export const QUINTESS_SUPPLIER_ID = '91';

/**
 * The supplier alone decides this layout. It is read from the property, which the
 * static call reports, so the page settles on one layout from the first paint; the
 * packages are the fallback for payloads that only carry it there.
 */
export const isQuintessVacationRental = (hotel: IHotelInfo | null | undefined): boolean =>
  hotel?.hotelDetails?.supplierId === QUINTESS_SUPPLIER_ID ||
  !!hotel?.adjustedPackageGroups?.some(({ groupedPackages }) =>
    groupedPackages?.some(({ supplierId }) => supplierId === QUINTESS_SUPPLIER_ID),
  );

const PERCENT = 100;

/** Grosses a supplier amount up by the marginator commission the account is selling at. */
export const applyMarginator = (
  amount: number | null | undefined,
  marginatorValue: number | null | undefined,
): number => (amount || 0) / (1 - (marginatorValue || 0) / PERCENT);

export const openHotelPage = (
  hotelId: number,
  history: H.History<H.LocationState>,
  disableNewTabOpen: boolean,
  isTravelAgent?: boolean,
  isVacationRentals?: boolean | null | undefined,
  isForceIframe?: boolean,
  sessionKey?: ISessionKey,
  certificateId?: string,
  certificateItemId?: string,
): void => {
  UrlUtils.removeFromUrl(ROOMS_SEARCH_LABEL);
  UrlUtils.removeFromUrl(SESSION_KEY_LABEL_ROOMS);
  UrlUtils.removeFromUrl( SESSION_KEY_LABEL);
  const routePath = getHotelRoutePath(hotelId, history, isVacationRentals, sessionKey);
  const trimmedId = certificateId?.trim();
  const navigationState = trimmedId
    ? {
        certificateId: trimmedId,
        ...(certificateItemId?.trim() ? { certificateItemId: certificateItemId.trim() } : {}),
      }
    : undefined;

  if (isTravelAgent || isForceIframe || disableNewTabOpen) {
    history.push(routePath, navigationState);
  } else {
    window.open(routePath, '_blank');
  }
};

export const isNotEmpty = (data: string): boolean => {
  return !isUndefined(data) && !isEmpty(data);
};

export const getFullAddress = (
  address: string,
  city: string,
  state: string,
  zipCode: string,
  countryCode: string,
  countryName = '',
): string[] => {
  const defaultBehaviorItems: string[] = [address, city, state, zipCode, countryName];
  const fullAddress: string[] = [];

  defaultBehaviorItems.forEach((item: string) => {
    if (isNotEmpty(item)) {
      fullAddress.push(item);
    }
  });

  if (isNotEmpty(countryCode) && countryName === '') {
    const country = countriesCode.find(
      ({ isoCode }) => isoCode.toLowerCase() === countryCode.toLowerCase(),
    );

    if (country) {
      fullAddress.push(country.name);
    } else {
      fullAddress.push(countryCode);
    }
  }

  return fullAddress;
};

const FREE_BREAKFAST = 'Free Breakfast';

const freeBreakfasts = [
  FREE_BREAKFAST,
  'Free Breakfast for 2',
  'American Breakfast',
  'Buffet Breakfast',
  'Continental Breakfast',
  'Continental Breakfast, Free Breakfast'
]

const freeBreakfastsSpanish = [
  "Desayuno gratis",
  "Desayuno gratis para 2",
  "Desayuno americano",
  "Desayuno buffet",
  "Desayuno continental",
  "Desayuno continental, desayuno gratis"
];

const isSpanish = ((localStorage.getItem('SELECTED_LANGUAGE_COUNTRY_LABEL')?.includes('es')) || (localStorage.getItem('SELECTED_LANGUAGE_LABEL')?.includes('es'))) ?? false;

export const isFreeBreakfastMealPlan = (mealPLan: string) => {
  return isSpanish ? freeBreakfastsSpanish.includes(mealPLan) : freeBreakfasts.includes(mealPLan);
}

export const isBreakfastMealPlan = (mealPLan: string) => {
  return mealPLan !== FREE_BREAKFAST && freeBreakfasts.includes(mealPLan);
}

export const isAllInclusivetMealPlan = (mealPLan: string) => {
  return mealPLan === 'All Inclusive' || mealPLan === 'All Inclusive, Spa Credit';
}

export enum WalletPriceType {
  Search,
  Details,
  Book,
};

export const getWalletPrices = (
      account: IAccount | null | undefined,
      userWalletData: IUserWallet | null | undefined,
      selectedClientCash: IClientCash | null | undefined,
      price: number | null | undefined,
      maxClientCash: number | null | undefined, 
      type: WalletPriceType) => {
  const walletNoDecimals = account?.walletNoDecimals;
  const walletWalletSavings = account?.walletWalletSavings;
  const isMLM = account?.isMLM;
  const convertionRate = userWalletData?.convertionRate ? userWalletData?.convertionRate : 1;
  
  const maxClientCashAmounParam = (maxClientCash && walletWalletSavings && !isMLM) ? Math.floor(maxClientCash) : maxClientCash;

  const priceCalculated = Math.floor((price ? price : 0) * 100) / 100;

  const maxClientCashPercentageAllow = userWalletData?.maxClientCashPercentageAllow ? userWalletData?.maxClientCashPercentageAllow : 1;
  const maxClientCashAmountAllowInt = priceCalculated * maxClientCashPercentageAllow;
  const maxClientCashAmountAllow = ((maxClientCashAmounParam || (maxClientCashAmounParam === 0 && walletWalletSavings)) && maxClientCashAmounParam < maxClientCashAmountAllowInt) ? maxClientCashAmounParam : maxClientCashAmountAllowInt;

  const maxClientCashAllowCalculatedData = maxClientCashAmountAllow / convertionRate;
  const maxClientCashAllowCalculated = walletNoDecimals ? Math.floor(maxClientCashAllowCalculatedData) : maxClientCashAllowCalculatedData;

  const maxClientCashAllow = walletNoDecimals ? Math.floor(maxClientCashAllowCalculated) : maxClientCashAllowCalculated;

  const selectedSearchClientCash = selectedClientCash?.selectedSearchClientCash ? selectedClientCash?.selectedSearchClientCash : 0;
  const selectedPropertyClientCash = selectedClientCash?.selectedPropertyClientCash ? selectedClientCash?.selectedPropertyClientCash : 0;
  const selectedPropertyReivewClientCash = selectedClientCash?.selectedPropertyReviewClientCash ? selectedClientCash?.selectedPropertyReviewClientCash : 0;
  const clientCash = type === WalletPriceType.Details ? 
                        selectedPropertyClientCash : 
                        type === WalletPriceType.Book ? 
                          selectedPropertyReivewClientCash :
                          selectedSearchClientCash;
  const clientCashAppliedCalculated = walletNoDecimals ? Math.floor(clientCash) : clientCash;
  const clientCashAmountCalculated = clientCashAppliedCalculated * convertionRate;
  const clientCashApplied = clientCashAmountCalculated > maxClientCashAmountAllow ? maxClientCashAllow : clientCashAppliedCalculated;
  const clientCashAmountAppliedInt = clientCashAmountCalculated > maxClientCashAmountAllow ? maxClientCashAmountAllow : clientCashAmountCalculated;
  const clientCashAmountApplied = clientCashAmountAppliedInt;
  const priceFinal = priceCalculated - (clientCashAmountApplied ? clientCashAmountApplied : 0);
  const priceFinalMax = priceCalculated - (maxClientCashAmountAllow ? maxClientCashAmountAllow : 0);
  const priceSavings = priceCalculated ? Math.floor(maxClientCashAmountAllow / priceCalculated * 100) : 0;

  const maxClientCashPermited = maxClientCashAllow > (userWalletData?.balance ? userWalletData?.balance : 0) ? userWalletData?.balance : maxClientCashAllow;
  const maxClientCashAmountPermited = (maxClientCashPermited ? maxClientCashPermited : 0) * convertionRate;

  return { 
    clientCashApplied,
    clientCashAmountApplied,
    maxClientCashAmountAllow,
    maxClientCashAllow,
    maxClientCashAmountPermited,
    maxClientCashPermited,
    priceFinal,
    priceFinalMax,
    convertionRate,
    priceSavings
  }; 
}